import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { Modal, Button, Alert } from 'react-bootstrap';
import { Save, X, Plus, Info, Upload, FileText, ExternalLink, Library } from 'lucide-react';
import { getStorage, ref as storageRef, getDownloadURL } from 'firebase/storage';
import { CostUnit, MaintenanceTemplate, EditForm, DirectionalInterval } from './types';
import { BaseType, BuildComponentType, CardinalDirection, MaintenanceIntervalType } from '../../../types/Enums';
import { translate } from '../../../types/EnumTranslations';
import { ORDERED_CARDINAL_DIRECTIONS } from '../../../Helpers/componentSortUtils';
import { defaultCostUnitForBaseType } from '../../../Helpers/baseTypeUtils';
import { translateIntervalType } from '../../../Helpers/maintenanceCostUtils';
import { maintenanceTemplateApi } from '../../../api/maintenanceTemplateApi';
import { tombstoneAttachmentDiff } from '../../../api/fileDeletionApi';
import { LibraryMaintenanceTemplate } from '../../../types/LibraryMaintenanceTemplate';
import { LibraryPickerModal } from '../../../Components/LibraryPickerModal';
import { maintenanceLibrary } from '../../../library/maintenanceLibrary';
import { useDragDrop } from '../../../hooks/useDragDrop';
import { UploadPickerModal } from '../../../Components/UploadPickerModal';
import NumberInput from '../../../Components/NumberInput';
import { CostHistoryButton } from '../../../Components/CostHistory/CostHistoryButton';

/** Building option for the building selector dropdown */
interface BuildingOption {
    key: string;
    name: string;
}

interface TemplateEditModalProps {
    show: boolean;
    template: MaintenanceTemplate | null;
    componentType: BuildComponentType | string;
    componentTypeKey: string;
    isNewTemplate: boolean;
    onSave: (template: EditForm) => Promise<void>;
    onCancel: () => void;
    saving: boolean;
    infoText?: string;
    /** Optional pre-translated name for display. If not provided, will translate componentType. */
    componentTypeName?: string;
    /** Optional building key for building-specific templates (used when creating from a building context) */
    buildingKey?: string;
    /** List of available buildings for the building selector (used in config page) */
    buildings?: BuildingOption[];
    /** Whether to show the building restriction UI (default: true when buildingKey or buildings provided) */
    showBuildingRestriction?: boolean;
    /** Base type of the component for library template suggestions */
    baseType?: BaseType;
    /** Name to prefill for a new template (e.g. the search term that found nothing) */
    initialName?: string;
}

const TemplateEditModal: React.FC<TemplateEditModalProps> = ({
    show,
    template,
    componentType,
    componentTypeKey,
    isNewTemplate,
    onSave,
    onCancel,
    saving,
    infoText,
    componentTypeName,
    buildingKey,
    buildings,
    showBuildingRestriction,
    baseType,
    initialName
}) => {
    const [editForm, setEditForm] = useState<EditForm>({
        name: '',
        description: '',
        interval_type: MaintenanceIntervalType.years,
        interval_value: 1,
        cost: 0,
        cost_unit: 'per_sqm',
        directionalIntervals: [],
        instructionalAttachments: {},
        primaryComponentType: componentTypeKey
    });
    const descriptionRef = useRef<HTMLTextAreaElement>(null);

    // Grow the description to fit its text, so a long description is shown in full
    useLayoutEffect(() => {
        const el = descriptionRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
    }, [editForm.description, show]);

    const [validationError, setValidationError] = useState<boolean>(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [uploading, setUploading] = useState<boolean>(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [openingAttachment, setOpeningAttachment] = useState<string | null>(null);
    const [buildingOnly, setBuildingOnly] = useState<boolean>(false);
    const [selectedBuildingKey, setSelectedBuildingKey] = useState<string>('');
    const [showLibraryPicker, setShowLibraryPicker] = useState<boolean>(false);
    const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
    // Pre-minted key for new templates so attachments upload to the template's
    // own Storage path before the template exists (no shared temp/ folder)
    const [draftTemplateKey, setDraftTemplateKey] = useState<string>('');

    const storage = getStorage();

    const handleFileUpload = async (file: File): Promise<void> => {
        const templateKey = template?.id || draftTemplateKey;
        if (!templateKey) {
            setUploadError('Fel vid uppladdning av fil');
            return;
        }

        try {
            setUploading(true);
            setUploadError(null);

            const { dbKey, attachment } = await maintenanceTemplateApi.uploadInstructionalAttachment(
                componentTypeKey,
                templateKey,
                file
            );

            setEditForm(prev => ({
                ...prev,
                instructionalAttachments: {
                    ...prev.instructionalAttachments,
                    [dbKey]: attachment
                }
            }));
        } catch (error) {
            console.error('Error uploading file:', error);
            setUploadError(error instanceof Error ? error.message : 'Fel vid uppladdning av fil');
        } finally {
            setUploading(false);
        }
    };

    const removeAttachment = (dbKey: string): void => {
        // An unsaved upload loses its only reference here — tombstone the blob
        // right away. Persisted attachments are tombstoned by updateTemplate
        // when the removal is actually saved.
        const attachment = editForm.instructionalAttachments[dbKey];
        if (attachment && !template?.instructionalAttachments?.[dbKey]) {
            void tombstoneAttachmentDiff('maintenanceTemplateAttachment', { [dbKey]: attachment }, undefined);
        }

        setEditForm(prev => {
            const newAttachments = { ...prev.instructionalAttachments };
            delete newAttachments[dbKey];
            return {
                ...prev,
                instructionalAttachments: newAttachments
            };
        });
    };

    const handleCancel = (): void => {
        // Unsaved uploads are abandoned with the modal — tombstone their blobs
        void tombstoneAttachmentDiff(
            'maintenanceTemplateAttachment',
            editForm.instructionalAttachments,
            template?.instructionalAttachments
        );
        onCancel();
    };

    const openAttachment = async (dbKey: string, storagePath: string): Promise<void> => {
        try {
            setOpeningAttachment(dbKey);
            const fileRef = storageRef(storage, storagePath);
            const downloadUrl = await getDownloadURL(fileRef);
            window.open(downloadUrl, '_blank');
        } catch (error) {
            console.error('Error opening attachment:', error);
            setUploadError('Kunde inte öppna filen');
        } finally {
            setOpeningAttachment(null);
        }
    };

    // Handle selected files (from upload modal or drag-and-drop)
    const handleFiles = useCallback((files: File[]) => {
        if (files.length === 0) return;

        const file = files[0];

        if (file.size > 10 * 1024 * 1024) {
            setUploadError('Filen är för stor. Maximal storlek är 10MB');
            return;
        }

        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf', 'text/plain'];
        if (!allowedTypes.includes(file.type)) {
            setUploadError('Filtypen stöds inte. Tillåtna format: JPEG, PNG, GIF, WebP, PDF, TXT');
            return;
        }

        handleFileUpload(file);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [template?.id, draftTemplateKey, componentTypeKey]);

    const { isDragOver, dragHandlers } = useDragDrop({ onFilesDropped: handleFiles });

    useEffect(() => {
        setShowLibraryPicker(false);
        if (show) {
            setValidationError(false);
            setSubmitError(null);
            setUploadError(null);
            if (template) {
                setEditForm({
                    name: template.name || '',
                    description: template.description || '',
                    interval_type: template.interval_type || MaintenanceIntervalType.years,
                    interval_value: template.interval_value || 1,
                    cost: template.cost || 0,
                    cost_unit: template.cost_unit || 'per_sqm',
                    directionalIntervals: template.directionalIntervals || [],
                    instructionalAttachments: template.instructionalAttachments || {},
                    primaryComponentType: template.primaryComponentType || componentTypeKey
                });
                // Initialize building restriction from existing template
                setBuildingOnly(!!template.buildingKey);
                setSelectedBuildingKey(template.buildingKey || '');
                setDraftTemplateKey('');
            } else {
                setDraftTemplateKey(maintenanceTemplateApi.newTemplateKey());
                // Reset for new template
                setEditForm({
                    name: initialName || '',
                    description: '',
                    interval_type: MaintenanceIntervalType.years,
                    interval_value: 1,
                    cost: 0,
                    cost_unit: defaultCostUnitForBaseType(baseType),
                    directionalIntervals: [],
                    instructionalAttachments: {},
                    primaryComponentType: componentTypeKey
                });
                setBuildingOnly(false);
                setSelectedBuildingKey(buildingKey || '');
            }
        }
    }, [template, show, buildingKey]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate required fields
        if (!editForm.name || editForm.name.trim() === '') {
            setValidationError(true);
            setSubmitError('Namn är obligatoriskt. Vänligen ange ett namn för mallen.');
            return;
        }

        if (!(editForm.interval_value >= 1)) {
            setSubmitError('Intervallet måste vara minst 1.');
            return;
        }

        // Validate building selection when building restriction is enabled with dropdown
        if (buildingOnly && buildings && buildings.length > 0 && !selectedBuildingKey) {
            setSubmitError('Vänligen välj en byggnad eller avmarkera byggnadsspecifik');
            return;
        }

        setValidationError(false);
        setSubmitError(null);

        // Determine the building key to use
        const effectiveBuildingKey = buildingOnly
            ? (selectedBuildingKey || buildingKey)
            : undefined;

        // Include buildingKey if checkbox is checked and a building is selected
        const formToSubmit: EditForm = {
            ...editForm,
            ...(effectiveBuildingKey && { buildingKey: effectiveBuildingKey }),
            // New templates must be saved under the key their attachments
            // were uploaded for
            ...(isNewTemplate && draftTemplateKey && { templateKey: draftTemplateKey })
        };

        try {
            await onSave(formToSubmit);
        } catch (error) {
            setSubmitError(error instanceof Error ? error.message : 'Kunde inte spara mallen. Försök igen.');
        }
    };

    const intervalTypeOptions = [
        { value: MaintenanceIntervalType.days, label: 'Dagar' },
        { value: MaintenanceIntervalType.weeks, label: 'Veckor' },
        { value: MaintenanceIntervalType.months, label: 'Månader' },
        { value: MaintenanceIntervalType.years, label: 'År' }
    ];

    const addDirectionalInterval = (): void => {
        const usedDirections = editForm.directionalIntervals.map(di => di.direction);
        const availableDirections = ORDERED_CARDINAL_DIRECTIONS.filter(
            direction => !usedDirections.includes(direction)
        );

        if (availableDirections.length > 0) {
            setEditForm(prev => ({
                ...prev,
                directionalIntervals: [
                    ...prev.directionalIntervals,
                    {
                        direction: availableDirections[0],
                        interval_value: prev.interval_value,
                        interval_type: prev.interval_type
                    }
                ]
            }));
        }
    };

    const updateDirectionalInterval = (index: number, field: keyof DirectionalInterval, value: any): void => {
        setEditForm(prev => ({
            ...prev,
            directionalIntervals: prev.directionalIntervals.map((di, i) =>
                i === index ? { ...di, [field]: value } : di
            )
        }));
    };

    const removeDirectionalInterval = (index: number): void => {
        setEditForm(prev => ({
            ...prev,
            directionalIntervals: prev.directionalIntervals.filter((_, i) => i !== index)
        }));
    };

    const handleLibrarySelect = (libTemplate: LibraryMaintenanceTemplate): void => {
        setEditForm(prev => ({
            ...prev,
            name: libTemplate.translations.sv,
            interval_type: libTemplate.interval_type,
            interval_value: libTemplate.interval_value,
            cost: libTemplate.cost,
            cost_unit: libTemplate.cost_unit,
        }));
        setShowLibraryPicker(false);
    };

    return (
        <Modal show={show} onHide={handleCancel} size="lg" centered dialogClassName="maintenance-templates-modal">
            <Modal.Header closeButton>
                <Modal.Title>
                    {isNewTemplate ? 'Skapa ny mall' : 'Redigera mall'} - {componentTypeName || translate(componentType as BuildComponentType)}
                </Modal.Title>
                {baseType && (
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm ms-auto me-3"
                        onClick={() => setShowLibraryPicker(true)}
                        disabled={saving}
                    >
                        <Library size={14} className="me-1" />
                        Kopiera från bibliotek
                    </button>
                )}
            </Modal.Header>
            <Modal.Body>
                {infoText && (
                    <div className="alert alert-info d-flex align-items-center mb-4">
                        <Info size={20} className="me-2 flex-shrink-0" />
                        <div>
                            {buildingOnly
                                ? 'Denna aktivitet kommer endast att visas för denna byggnad'
                                : infoText}
                        </div>
                    </div>
                )}

                {/* Building-specific checkbox - show when buildingKey or buildings list is provided */}
                {(showBuildingRestriction !== false) && (buildingKey || (buildings && buildings.length > 0)) && (
                    <div className="mb-4 p-3 border rounded" style={{ background: '#f8f9fa' }}>
                        <div className="form-check mb-2">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id="building-only-checkbox"
                                checked={buildingOnly}
                                onChange={(e) => {
                                    setBuildingOnly(e.target.checked);
                                    if (!e.target.checked) {
                                        setSelectedBuildingKey('');
                                    }
                                }}
                            />
                            <label className="form-check-label" htmlFor="building-only-checkbox">
                                Begränsa till specifik byggnad
                            </label>
                        </div>
                        <div className="form-text text-muted small mb-2">
                            Om markerad kommer mallen endast visas för den valda byggnaden
                        </div>

                        {/* Building selector dropdown - show when buildings list is provided and checkbox is checked */}
                        {buildingOnly && buildings && buildings.length > 0 && (
                            <div className="mt-3">
                                <label className="form-label small fw-medium">Välj byggnad</label>
                                <select
                                    className="form-select form-select-sm"
                                    value={selectedBuildingKey}
                                    onChange={(e) => setSelectedBuildingKey(e.target.value)}
                                >
                                    <option value="">-- Välj byggnad --</option>
                                    {buildings.map(b => (
                                        <option key={b.key} value={b.key}>{b.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Show info when building is pre-selected (from building context) */}
                        {buildingOnly && buildingKey && !buildings && (
                            <div className="mt-2 small text-muted">
                                Mallen kommer att begränsas till denna byggnad
                            </div>
                        )}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    {/* Basic Template Fields */}
                    <div className="row g-3 mb-4">
                        <div className="col-12">
                            <label className="form-label fw-medium">
                                Namn <span className="text-danger">*</span>
                            </label>
                            <input
                                type="text"
                                className={`form-control ${validationError && (!editForm.name || editForm.name.trim() === '') ? 'is-invalid' : ''}`}
                                value={editForm.name}
                                onChange={(e) => {
                                    setEditForm(prev => ({ ...prev, name: e.target.value }));
                                    if (validationError && e.target.value.trim() !== '') {
                                        setValidationError(false);
                                    }
                                }}
                                placeholder="T.ex. Fönstermålning"
                                required
                            />
                            {validationError && (!editForm.name || editForm.name.trim() === '') && (
                                <div className="invalid-feedback d-block">
                                    Namn är obligatoriskt
                                </div>
                            )}
                        </div>
                        <div className="col-12">
                            <label className="form-label fw-medium">Beskrivning</label>
                            <textarea
                                ref={descriptionRef}
                                className="form-control"
                                rows={2}
                                style={{ resize: 'none', overflow: 'hidden' }}
                                value={editForm.description}
                                onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                                placeholder="Valfri beskrivning"
                            />
                        </div>
                    </div>

                    {/* Interval Fields */}
                    <div className="row g-3 mb-4">
                        <div className="col-md-4">
                            <label className="form-label fw-medium">Intervall</label>
                            <NumberInput
                                min="1"
                                className="form-control"
                                integer
                                emptyValue={1}
                                value={editForm.interval_value}
                                onChange={(value) => setEditForm(prev => ({ ...prev, interval_value: value }))}
                                required
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="form-label fw-medium">Enhet</label>
                            <select
                                className="form-select"
                                value={editForm.interval_type}
                                onChange={(e) => setEditForm(prev => ({ ...prev, interval_type: e.target.value as MaintenanceIntervalType }))}
                            >
                                {intervalTypeOptions.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-4">
                            <label className="form-label fw-medium">Kostnadsenhet</label>
                            <select
                                className="form-select"
                                value={editForm.cost_unit}
                                onChange={(e) => setEditForm(prev => ({ ...prev, cost_unit: e.target.value as CostUnit }))}
                            >
                                <option value="per_sqm">kr/kvm</option>
                                <option value="per_unit">kr/st</option>
                                <option value="per_lm">kr/lm</option>
                            </select>
                        </div>
                    </div>

                    <div className="row g-3 mb-4">
                        <div className="col-md-12">
                            <div className="d-flex justify-content-between align-items-baseline">
                                <label className="form-label fw-medium">Kostnad ({editForm.cost_unit === 'per_sqm' ? 'kr/kvm' : editForm.cost_unit === 'per_lm' ? 'kr/lm' : 'kr/st'})</label>
                                {!isNewTemplate && template?.id && (
                                    <CostHistoryButton kind="templates" subjectKey={template.id} title={template.name} />
                                )}
                            </div>
                            <NumberInput
                                min="0"
                                step="any"
                                className="form-control"
                                value={editForm.cost}
                                onChange={(value) => setEditForm(prev => ({ ...prev, cost: value }))}
                                required
                            />
                        </div>
                    </div>

                    {/* Directional Intervals */}
                    <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0 fw-semibold" style={{ color: '#495057' }}>Väderstrecksberoende intervall (valfritt)</h6>
                            {ORDERED_CARDINAL_DIRECTIONS.filter(dir =>
                                !editForm.directionalIntervals.some(di => di.direction === dir)
                            ).length > 0 && (
                                    <button
                                        onClick={addDirectionalInterval}
                                        className="btn btn-outline-secondary btn-sm"
                                        type="button"
                                    >
                                        <Plus size={14} className="me-1" />
                                        Lägg till väderstreck
                                    </button>
                                )}
                        </div>

                        {editForm.directionalIntervals.length === 0 && (
                            <div className="text-muted fst-italic small mb-3">
                                Använder standard intervall ({editForm.interval_value} {translateIntervalType(editForm.interval_type)}) för alla väderstreck
                            </div>
                        )}

                        {editForm.directionalIntervals.map((interval, index) => {
                            const usedDirections = editForm.directionalIntervals.map(di => di.direction);
                            const availableDirections = ORDERED_CARDINAL_DIRECTIONS.filter(
                                direction => !usedDirections.includes(direction) || direction === interval.direction
                            );

                            return (
                                <div key={index} className="card mb-2" style={{ background: '#fafbfc', border: '1px solid #e9ecef' }}>
                                    <div className="card-body p-3">
                                        <div className="row g-2 align-items-center">
                                            <div className="col-md-3">
                                                <label className="form-label small fw-medium mb-1">Väderstreck</label>
                                                <select
                                                    className="form-select form-select-sm"
                                                    value={interval.direction}
                                                    onChange={(e) => updateDirectionalInterval(index, 'direction', e.target.value as CardinalDirection)}
                                                >
                                                    {availableDirections.map(direction => (
                                                        <option key={direction} value={direction}>
                                                            {translate(direction)}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="col-md-4">
                                                <label className="form-label small fw-medium mb-1">Intervall</label>
                                                <NumberInput
                                                    min="1"
                                                    className="form-control form-control-sm"
                                                    integer
                                                    emptyValue={1}
                                                    value={interval.interval_value}
                                                    onChange={(value) => updateDirectionalInterval(index, 'interval_value', value)}
                                                />
                                            </div>
                                            <div className="col-md-3">
                                                <label className="form-label small fw-medium mb-1">Enhet</label>
                                                <select
                                                    className="form-select form-select-sm"
                                                    value={interval.interval_type}
                                                    onChange={(e) => updateDirectionalInterval(index, 'interval_type', e.target.value as MaintenanceIntervalType)}
                                                >
                                                    {intervalTypeOptions.map(option => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="col-md-2 d-flex align-items-end">
                                                <button
                                                    onClick={() => removeDirectionalInterval(index)}
                                                    className="btn btn-outline-danger btn-sm w-100"
                                                    type="button"
                                                >
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        {editForm.directionalIntervals.length > 0 && (
                            <div className="alert alert-info small mt-2 mb-0">
                                <strong>Info:</strong> Väderstreck utan specificerat intervall använder standard intervallet ({editForm.interval_value} {translateIntervalType(editForm.interval_type)}).
                            </div>
                        )}
                    </div>

                    {/* Attachments Section */}
                    <div
                        {...dragHandlers}
                        style={{
                            padding: '12px',
                            marginBottom: '1rem',
                            borderRadius: '8px',
                            border: isDragOver ? '2px dashed #007bff' : '2px dashed #dee2e6',
                            backgroundColor: isDragOver ? '#f0f8ff' : 'transparent',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0 fw-semibold" style={{ color: '#495057' }}>Bilagor (valfritt)</h6>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => setShowUploadModal(true)}
                                disabled={uploading || saving}
                            >
                                <Upload size={14} className="me-1" />
                                Ladda upp fil
                            </Button>
                        </div>

                        {uploadError && (
                            <div className="alert alert-danger small py-2 mb-3">
                                {uploadError}
                            </div>
                        )}

                        {Object.keys(editForm.instructionalAttachments).length === 0 && (
                            <div className="text-muted fst-italic small mb-3">
                                Inga bilagor bifogade ännu. Du kan också dra och släppa filer här.
                            </div>
                        )}

                        {Object.entries(editForm.instructionalAttachments).map(([dbKey, attachment]) => (
                            <div key={dbKey} className="card mb-2" style={{ background: '#fafbfc', border: '1px solid #e9ecef' }}>
                                <div className="card-body p-3">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center">
                                            <FileText size={16} className="text-muted me-2" />
                                            <div>
                                                <div className="fw-medium small">{attachment.key}</div>
                                                <div className="text-muted small">
                                                    Uppladdad: {new Date(attachment.date).toLocaleDateString('sv-SE')}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="btn-group btn-group-sm">
                                            <button
                                                onClick={() => openAttachment(dbKey, attachment.storagePath)}
                                                className="btn btn-outline-secondary"
                                                type="button"
                                                disabled={uploading || saving || openingAttachment === dbKey}
                                            >
                                                {openingAttachment === dbKey ? (
                                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                ) : (
                                                    <ExternalLink size={14} />
                                                )}
                                            </button>
                                            <button
                                                onClick={() => removeAttachment(dbKey)}
                                                className="btn btn-outline-danger"
                                                type="button"
                                                disabled={uploading || saving}
                                            >
                                                <X size={14} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </form>
            </Modal.Body>
            <Modal.Footer>
                {submitError && (
                    <Alert variant="danger" className="w-100 small py-2 mb-2">
                        {submitError}
                    </Alert>
                )}
                <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={handleCancel}
                    disabled={saving}
                >
                    <X size={16} className="me-1" />
                    Avbryt
                </button>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSubmit}
                    disabled={saving}
                >
                    {saving ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Sparar...
                        </>
                    ) : (
                        <>
                            <Save size={16} className="me-1" />
                            Spara
                        </>
                    )}
                </button>
            </Modal.Footer>

            {/* Library Template Picker Modal */}
            {baseType && (
                <LibraryPickerModal
                    config={maintenanceLibrary}
                    open={showLibraryPicker}
                    onClose={() => setShowLibraryPicker(false)}
                    onSelect={handleLibrarySelect}
                    currentBaseType={baseType}
                />
            )}

            {/* Upload Picker Modal */}
            <UploadPickerModal
                show={showUploadModal}
                onHide={() => setShowUploadModal(false)}
                onFilesSelected={handleFiles}
                accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.txt"
                multiple={false}
                title="Ladda upp"
            />
        </Modal>
    );
};

export default TemplateEditModal;

